import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFanBeltLength } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.sheaveFantBeltLength.v1';

type Fields = { pd1: string; pd2: string; centerDistance: string; beltLength: string };
const EMPTY: Fields = { pd1: '', pd2: '', centerDistance: '', beltLength: '' };

/**
 * Fan Belt Length — L = 2C + 1.57(PD₁+PD₂) − (PD₁−PD₂)²/(4C)
 * Bidirectional 4-field solver.
 *  - Missing L: direct
 *  - Missing C: quadratic 8C² + (6.28·S − 4L)C − D² = 0 (positive root)
 *  - Missing PD₁ or PD₂: quadratic in the unknown pulley
 */
const SheaveEquationsFanBeltLengthScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const solveForPulley = (pKnown: number, c: number, l: number): number | null => {
    // (x - pK)² = 4C·[2C + 1.57(x + pK) − L]
    // x² − (2pK + 6.28C) x + (pK² − 8C² − 6.28C·pK + 4CL) = 0
    const a = 1;
    const b = -(2 * pKnown + 6.28 * c);
    const cc = pKnown * pKnown - 8 * c * c - 6.28 * c * pKnown + 4 * c * l;
    const disc = b * b - 4 * a * cc;
    if (disc < 0) return null;
    const root1 = (-b + Math.sqrt(disc)) / 2;
    const root2 = (-b - Math.sqrt(disc)) / 2;
    // Prefer the positive physical root
    if (root1 > 0 && root2 > 0) return Math.min(root1, root2); // pulley smaller than other+2C typically
    return root1 > 0 ? root1 : (root2 > 0 ? root2 : null);
  };

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) { setError('Fill any 3 of the 4 fields to solve for the missing one.'); return; }
    if (filled === 4) { setError('All 4 values are filled — clear one to solve for it.'); return; }

    const p1 = toImp(fields.pd1, 'diameter');
    const p2 = toImp(fields.pd2, 'diameter');
    const c = toImp(fields.centerDistance, 'diameter');
    const l = toImp(fields.beltLength, 'diameter');

    if (fields.beltLength === '') {
      if (!(p1 > 0) || !(p2 > 0) || !(c > 0)) { setError('All dimensions must be greater than zero.'); return; }
      const L = 2 * c + 1.57 * (p1 + p2) - Math.pow(p1 - p2, 2) / (4 * c);
      setSolvedFor('beltLength');
      set({ beltLength: fromImp(L, 'diameter', 2) });
    } else if (fields.centerDistance === '') {
      if (!(p1 > 0) || !(p2 > 0) || !(l > 0)) { setError('Pulleys and belt length must be greater than zero.'); return; }
      // 8C² + (6.28·S − 4L)C − D² = 0
      const S = p1 + p2;
      const D = p1 - p2;
      const A = 8;
      const B = 6.28 * S - 4 * l;
      const C = -D * D;
      const disc = B * B - 4 * A * C;
      if (disc < 0) { setError('No real solution for center distance with those pulleys and belt length.'); return; }
      const C1 = (-B + Math.sqrt(disc)) / (2 * A);
      if (!(C1 > 0)) { setError('Belt length is too short for that pulley pair.'); return; }
      setSolvedFor('centerDistance');
      set({ centerDistance: fromImp(C1, 'diameter', 2) });
    } else if (fields.pd1 === '') {
      if (!(p2 > 0) || !(c > 0) || !(l > 0)) { setError('Other values must be greater than zero.'); return; }
      const x = solveForPulley(p2, c, l);
      if (x == null || x <= 0) { setError('No real solution for PD₁ with those inputs.'); return; }
      setSolvedFor('pd1');
      set({ pd1: fromImp(x, 'diameter', 2) });
    } else if (fields.pd2 === '') {
      if (!(p1 > 0) || !(c > 0) || !(l > 0)) { setError('Other values must be greater than zero.'); return; }
      const x = solveForPulley(p1, c, l);
      if (x == null || x <= 0) { setError('No real solution for PD₂ with those inputs.'); return; }
      setSolvedFor('pd2');
      set({ pd2: fromImp(x, 'diameter', 2) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.pd1 && { label: 'Pulley 1', value: fields.pd1, unit: unit('diameter'), emphasis: solvedFor === 'pd1' },
    fields.pd2 && { label: 'Pulley 2', value: fields.pd2, unit: unit('diameter'), emphasis: solvedFor === 'pd2' },
    fields.centerDistance && { label: 'Center Distance', value: fields.centerDistance, unit: unit('diameter'), emphasis: solvedFor === 'centerDistance' },
    fields.beltLength && { label: 'Belt Length', value: fields.beltLength, unit: unit('diameter'), emphasis: solvedFor === 'beltLength' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Fan Belt Length"
      subtitle="Solve for belt length, center distance, or either pulley — fill any 3 of 4"
      formula={
        <>
          <FormulaLine>L = 2C + 1.57(PD₁ + PD₂) − (PD₁ − PD₂)² / (4C)</FormulaLine>
          <FormulaNote>C = Center distance, PD₁/PD₂ = Pulley diameters. All in {unit('diameter')}.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: L = 2C + 1.57(PD₁+PD₂) − (PD₁−PD₂)²/(4C)"
      diagram={<DiagramFanBeltLength />}
      outputSlots={{
        fan_pd_in: fields.pd1 ? toImp(fields.pd1, 'diameter') : undefined,
        motor_pd_in: fields.pd2 ? toImp(fields.pd2, 'diameter') : undefined,
        center_distance_in: fields.centerDistance ? toImp(fields.centerDistance, 'diameter') : undefined,
        belt_length_in: fields.beltLength ? toImp(fields.beltLength, 'diameter') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.fan_pd_in != null && !fields.pd1) patch.pd1 = fromImp(values.fan_pd_in, 'diameter', 2);
        if (values.motor_pd_in != null && !fields.pd2) patch.pd2 = fromImp(values.motor_pd_in, 'diameter', 2);
        if (values.center_distance_in != null && !fields.centerDistance) patch.centerDistance = fromImp(values.center_distance_in, 'diameter', 2);
        if (values.belt_length_in != null && !fields.beltLength) patch.beltLength = fromImp(values.belt_length_in, 'diameter', 2);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Pulley Diameter 1 (${unit('diameter')})`} value={fields.pd1} onChangeText={(v) => set({ pd1: v })} rightText={unit('diameter')} maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Pulley Diameter 2 (${unit('diameter')})`} value={fields.pd2} onChangeText={(v) => set({ pd2: v })} rightText={unit('diameter')} maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Center Distance (${unit('diameter')})`} value={fields.centerDistance} onChangeText={(v) => set({ centerDistance: v })} rightText={unit('diameter')} maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Belt Length (${unit('diameter')})`} value={fields.beltLength} onChangeText={(v) => set({ beltLength: v })} rightText={unit('diameter')} maxDecimals={3} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default SheaveEquationsFanBeltLengthScreen;
